/**
 * Fuel.tsx
 * Log a retail fuel purchase.
 * Fields: Date, PaidBy (dropdown of partners, default = logged-in partner),
 *   Airport (text, default from config), Gallons, TotalCost (required),
 *   optional Receipt photo, Notes.
 *
 * Co-op fuel is logged as an Expense (one row per month, vendor-credited).
 * Every Fuel form entry is always Retail — no Source selector needed.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { getConfig, submitFuel, readFuelReceipt, ApiError } from '../api'
import PhotoCapture from '../components/PhotoCapture'
import type { Partner } from '../types'

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

type PageState = 'form' | 'submitting' | 'done'

export default function Fuel() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [partners, setPartners] = useState<Partner[]>([])
  const [date, setDate] = useState(today())
  const [paidBy, setPaidBy] = useState('')
  const [airport, setAirport] = useState('')
  const [gallons, setGallons] = useState('')
  const [totalCost, setTotalCost] = useState('')
  const [notes, setNotes] = useState('')
  const [receiptKey, setReceiptKey] = useState<string | undefined>(undefined)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [pageState, setPageState] = useState<PageState>('form')

  // ---- OCR status (after a receipt photo is uploaded) ---------------------
  // Reads vendor/gallons/totalCost/date/airport from the uploaded fuel receipt
  // and prefills the form fields. Whether or not OCR succeeds, the partner
  // still gets the manual entry path and the photo is still stored.
  type OcrState = 'idle' | 'reading' | 'done' | 'error'
  const [ocrState, setOcrState] = useState<OcrState>('idle')
  const [ocrMsg, setOcrMsg] = useState<string>('')

  // Load partners + last airport. Backend tells us who we are (caller.id),
  // so we don't have to guess by email/name.
  useEffect(() => {
    getConfig()
      .then((cfg) => {
        setPartners(cfg.partners)
        setAirport(cfg.lastAirport ?? '')
        setPaidBy(cfg.caller?.id ?? '')
      })
      .catch(() => {
        // Non-fatal; user can fill fields manually
      })
  }, [user])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!date) return setError('Date is required.')
    const gal = parseFloat(gallons)
    if (!gallons || isNaN(gal) || gal <= 0)
      return setError('Gallons is required and must be a positive number.')
    if (!totalCost) return setError('Total Cost is required.')
    const cost = parseFloat(totalCost)
    if (isNaN(cost) || cost <= 0)
      return setError('Total Cost must be a positive number.')

    setPageState('submitting')
    try {
      const payload = {
        date,
        paidBy,
        airport,
        gallons: gal,
        totalCost: cost,
        notes,
        ...(receiptKey ? { receiptKey } : {}),
      }
      const result = await submitFuel(payload)
      if (!result.ok) {
        setError(result.message || 'Could not save.')
        setPageState('form')
        return
      }
      setSuccessMsg(result.message)
      setPageState('done')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        navigate('/not-authorized')
        return
      }
      setError(err instanceof Error ? err.message : 'Unexpected error.')
      setPageState('form')
    }
  }

  function handleLogAnother() {
    setDate(today())
    setGallons('')
    setTotalCost('')
    setNotes('')
    setReceiptKey(undefined)
    setError('')
    setSuccessMsg('')
    setOcrState('idle')
    setOcrMsg('')
    setPageState('form')
  }

  /**
   * Called by PhotoCapture after the receipt is uploaded. Stores the S3 key
   * so submitFuel includes it AND fires the readFuelReceipt OCR to prefill
   * any empty form fields. Existing partner-entered values are never
   * overwritten — the partner is the source of truth, the OCR is help.
   */
  async function handleReceiptUploaded(key: string): Promise<void> {
    setReceiptKey(key)
    setOcrState('reading')
    setOcrMsg('Reading receipt…')
    try {
      const r = await readFuelReceipt(key)
      if (r.confidence === 'low') {
        setOcrState('error')
        setOcrMsg(
          r.reason
            ? `Could not read receipt (${r.reason}). Enter values manually.`
            : 'Could not read this receipt. Enter values manually.',
        )
        return
      }
      // Prefill only the fields the partner hasn't already filled in. The
      // model returns null for fields it couldn't read; skip those.
      const filled: string[] = []
      if (r.gallons != null && gallons === '') {
        setGallons(String(r.gallons))
        filled.push('Gallons')
      }
      if (r.totalUSD != null && totalCost === '') {
        setTotalCost(String(r.totalUSD))
        filled.push('Total Cost')
      }
      if (r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date) && date === today()) {
        // Only overwrite the default-today date — don't touch a date the
        // partner has already adjusted.
        setDate(r.date)
        filled.push('Date')
      }
      if (r.airport && (airport === '' || airport.length < 3)) {
        setAirport(r.airport.toUpperCase())
        filled.push('Airport')
      }
      const verb = r.confidence === 'high' ? 'Filled' : 'Suggested (please verify):'
      setOcrState('done')
      setOcrMsg(
        filled.length > 0
          ? `${verb} ${filled.join(', ')}.`
          : 'Receipt uploaded; no new fields to fill.',
      )
    } catch (err) {
      setOcrState('error')
      setOcrMsg(err instanceof Error ? err.message : 'Receipt read failed.')
    }
  }


  return (
    <div className="wrap">
      <div className="brand">
        <div className="brand-logo">N73AA</div>
        <div className="brand-tag">
          <Link to="/" className="back-link">&#8592; Back</Link>
          &ensp;Beechcraft Bonanza N35 &mdash; Log Fuel
        </div>
      </div>

      {user && (
        <div className="who">
          Logging as: <strong>{user.name}</strong>
        </div>
      )}

      {pageState !== 'done' ? (
        <form className="card" onSubmit={handleSubmit} noValidate>
          {error && <div className="msg msg-err">{error}</div>}

          <label htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            autoFocus
          />

          <label htmlFor="paidBy">Paid By</label>
          <select
            id="paidBy"
            value={paidBy}
            onChange={(e) => setPaidBy(e.target.value)}
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.id})
              </option>
            ))}
          </select>

          <label htmlFor="airport">Airport</label>
          <input
            id="airport"
            type="text"
            value={airport}
            onChange={(e) => setAirport(e.target.value.toUpperCase())}
            placeholder="e.g. KSGR"
            maxLength={10}
          />

          <label htmlFor="gallons">Gallons</label>
          <input
            id="gallons"
            type="number"
            step="0.01"
            inputMode="decimal"
            value={gallons}
            onChange={(e) => setGallons(e.target.value)}
            placeholder="e.g. 24.5"
            required
          />

          <label htmlFor="totalCost">Total Cost ($)</label>
          <input
            id="totalCost"
            type="number"
            step="0.01"
            inputMode="decimal"
            value={totalCost}
            onChange={(e) => setTotalCost(e.target.value)}
            placeholder="e.g. 145.00"
            required
          />

          <label>Receipt Photo (optional)</label>
          <PhotoCapture
            label="fuel"
            date={date}
            partnerId={paidBy}
            buttonLabel="📷 Take fuel-receipt photo (auto-fills fields)"
            onUploadComplete={handleReceiptUploaded}
            onUploadError={(msg) => {
              setOcrState('error')
              setOcrMsg(msg)
            }}
          />
          {ocrMsg && (
            <p
              className="field-note"
              style={{ color: ocrState === 'error' ? '#c5221f' : '#137333' }}
            >
              {ocrMsg}
            </p>
          )}

          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Remarks…"
          />

          <button
            className="btn btn-fuel"
            type="submit"
            disabled={pageState === 'submitting'}
          >
            {pageState === 'submitting' ? 'Saving…' : 'Log Fuel'}
          </button>
          {pageState === 'submitting' && (
            <div className="spin">Saving to the log…</div>
          )}
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="card">
          <div className="msg msg-ok">{successMsg}</div>
          <div className="confirm-actions">
            <button className="btn btn-fuel" onClick={handleLogAnother}>
              Log another fuel purchase
            </button>
            <button className="btn btn-neutral" onClick={() => navigate('/')}>
              Back to menu
            </button>
          </div>
        </div>
      )}

      <div className="page-foot">N73AA partnership log.</div>
    </div>
  )
}
